import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';
import { EMPTY, finalize, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AppService } from '@services/app.service';

import { environment } from '../../environments/environment';

import { ErrorAlertStateService } from './error-alert-state.service';

const SKIP_PATTERNS: readonly (string | RegExp)[] = [
  'geocoding/reverse/',
  'drive-sense/api/',
  'geozone/objects_inside/',
  'routes/build/',
  'account_icons/',
  /reports\/export\/(xlsx|csv|pdf)\//,
];

interface ErrorContext {
  readonly alerts: TuiAlertService;
  readonly translate: TranslateService;
  readonly appService: AppService;
  readonly state: ErrorAlertStateService;
}

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  if (shouldSkip(request)) {
    return next(request);
  }

  const context: ErrorContext = {
    alerts: inject(TuiAlertService),
    translate: inject(TranslateService),
    appService: inject(AppService),
    state: inject(ErrorAlertStateService),
  };

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      const errorObj = error.error;
      const result = errorHandlerByStatus(context, error.status, errorObj);
      if (result) {
        return throwError(() => errorObj);
      }
      return EMPTY;
    }),
  );
};

function shouldSkip(request: HttpRequest<unknown>): boolean {
  return SKIP_PATTERNS.some((pattern) => {
    if (typeof pattern === 'string') {
      return request.url.includes(pattern);
    }
    return pattern.test(request.url);
  });
}

function errorHandlerByStatus(context: ErrorContext, status: number, errorObj: any): boolean {
  context.appService.isLoading.next(false);
  switch (status) {
    case 400:
      return status400(context, errorObj);
    case 401:
      status401();
      break;
    case 403:
      status403(context, errorObj);
      break;
    case 404:
      return status404(context);
    case 410:
    case 418:
      throw errorObj;
    case 413:
      status413(context);
      break;
    case 423:
      status423(context, errorObj);
      break;
    case 429:
      status429(context);
      break;
    case 500:
      showTranslatedNotification(context, 'err500Label', 'err500Content');
      break;
    case 503:
      showTranslatedNotification(context, 'err503Label', 'err503Content');
      break;
    default:
      showNotification(context);
  }
  return false;
}

function status400(context: ErrorContext, errorObj: any): boolean {
  const label = context.translate.instant('err400Label');

  const showErrorNotification = (content: string) => {
    showNotification(context, label, content);
  };

  if (errorObj?.detail?.length > 0) {
    errorObj.detail.forEach(showErrorNotification);
  } else if (errorObj?.error) {
    const errorMessage = errorObj.error.message || errorObj.error;
    showErrorNotification(errorMessage);
  }

  return true;
}

function status401(): void {
  sessionStorage.clear();
  window.location.href = '/';
}

function status403(context: ErrorContext, errorObj: any): void {
  const label = context.translate.instant('err403Label');
  const content = context.translate.instant('err403Content');
  if (errorObj?.error) {
    showNotification(context, label, errorObj?.error);
  } else {
    showNotification(context, label, content);
  }
}

function status404(context: ErrorContext): boolean {
  showTranslatedNotification(context, 'err404Label', 'err404Content');

  return true;
}

function status413(context: ErrorContext): void {
  showTranslatedNotification(context, 'Error uploading file', 'err413Content');
}

function status423(context: ErrorContext, errorObj: any): void {
  const content = errorObj?.detail || context.translate.instant('The account is blocked');
  if (sessionStorage.getItem(environment.tokenKey)) {
    sessionStorage.clear();
    window.location.href = `/auth/login/?eventType=deactivate`;
  } else {
    showNotification(context, '', content);
  }
}

function status429({ alerts, translate, state }: ErrorContext): void {
  if (state.is429AlertActive) {
    return;
  }

  state.is429AlertActive = true;

  alerts
    .open(translate.instant('The request limit has been exceeded'), {
      autoClose: 6000,
      appearance: 'error',
    })
    .pipe(finalize(() => (state.is429AlertActive = false)))
    .subscribe();
}

function showTranslatedNotification(
  context: ErrorContext,
  labelKey: string,
  contentKey: string,
): void {
  showNotification(
    context,
    context.translate.instant(labelKey),
    context.translate.instant(contentKey),
  );
}

function showNotification(
  { alerts }: ErrorContext,
  label = 'Error application',
  content = '',
  autoClose: boolean | number = 5000,
): void {
  alerts
    .open(content, {
      label,
      autoClose: autoClose === false ? 0 : autoClose === true ? 3000 : autoClose,
      appearance: 'error',
    })
    .subscribe();
}
